import { useApolloClient, useMutation } from '@apollo/client'
import { emojiIndex } from 'emoji-mart'
import {
  CommunityPostDataFragmentDoc,
  CreateReactionDocument,
  DeleteReactionDocument,
} from 'generated/resources/graphql'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'

import { COMMUNITY_ITEM_TYPES, COMMUNITY_WIDGET_TYPES } from 'modules/dashboard/lib/events'

type AddReactionParams = {
  postId: number
  code: string
  reaction: {
    id: number
    author_id: number
  }
}

type RemoveReactionFromPostParams = {
  postId: number
  code: string
}

type UseReactionsParams = {
  postId: number
}

export const textCodeToEmoji = (textCode: string) => {
  const emojiData = emojiIndex.search(textCode)?.[0]

  return emojiData && 'native' in emojiData ? emojiData.native : undefined
}

export const emojiToTextCode = (nativeEmoji: string) => {
  let emojiCode: string | undefined

  const allEmojis = emojiIndex.emojis
  const emojiData = Object.values(allEmojis).find((emoji) => {
    const found = 'native' in emoji && emoji.native === nativeEmoji

    if (found) return true

    if (1 in emoji) {
      return Object.values(emoji).find((em) => {
        return 'native' in em && em.native === nativeEmoji
      })
    }
  })

  if (!emojiData) return

  if ('colons' in emojiData) {
    emojiCode = emojiData.colons
  } else if (1 in emojiData) {
    const e = Object.values(emojiData).find((em) => {
      return 'native' in em && em.native === nativeEmoji
    })
    emojiCode = e && 'colons' in e ? e.colons : undefined
  }

  return emojiCode?.slice(1, emojiCode.length - 1)
}

export const useReactions = ({ postId }: UseReactionsParams) => {
  const { access } = useCurrentSession()

  const authorId = access.id

  const apolloClient = useApolloClient()

  const [createReactionMutation] = useMutation(CreateReactionDocument)

  const [deleteReactionMutation] = useMutation(DeleteReactionDocument)

  const getCachedPost = () => {
    return apolloClient.readFragment({
      fragment: CommunityPostDataFragmentDoc,
      id: `PostsPost:${postId}`,
    })
  }

  const removeReactionFromPost = ({ code }: RemoveReactionFromPostParams) => {
    const fragmentData = getCachedPost()

    if (!fragmentData || !authorId) {
      return
    }

    const reactions = fragmentData.newReactions ?? []

    const reacted = reactions.find(
      (reaction) =>
        reaction.code.startsWith(code) &&
        reaction.supporters.some((supporter) => supporter.accessId === authorId)
    )

    if (!reacted) {
      return
    }

    const newReactionsToStore = reactions.map((reaction) => {
      if (
        !reaction.code.startsWith(code) ||
        !reaction.supporters.some((supporter) => supporter.accessId === authorId)
      ) {
        return reaction
      }

      return {
        ...reaction,
        count: reaction.count - 1,
        supporters: reaction.supporters.filter((supporter) => supporter.accessId !== authorId),
      }
    })

    apolloClient.writeFragment({
      fragment: CommunityPostDataFragmentDoc,
      id: `PostsPost:${postId}`,
      fragmentName: 'CommunityPostData',
      data: {
        ...fragmentData,
        reactionsCount: fragmentData.reactionsCount - 1,
        newReactions: newReactionsToStore,
      },
    })
  }

  const addReactionToPost = ({ code, reaction: newReaction }: AddReactionParams) => {
    const fragmentData = getCachedPost()

    if (!fragmentData) {
      return
    }

    const reactions = fragmentData.newReactions ?? []

    const reacted = reactions.find(
      (reaction) =>
        reaction.code.startsWith(code) &&
        reaction.supporters.some((supporter) => supporter.accessId === newReaction.author_id)
    )

    if (reacted) {
      return
    }

    const reactionWithCode = reactions.find((reaction) => reaction.code.startsWith(code))

    let newReactionsToStore = reactions

    const newSupporter = {
      id: newReaction.id,
      accessId: access.id,
      fullName: access.fullName,
    }

    if (reactionWithCode) {
      newReactionsToStore = newReactionsToStore.map((reaction) => {
        if (!reaction.code.startsWith(code)) {
          return reaction
        }

        return {
          ...reaction,
          count: reaction.count + 1,
          supporters: [...reaction.supporters, newSupporter],
        }
      })
    } else {
      newReactionsToStore = [
        ...newReactionsToStore,
        {
          code,
          postId,
          count: 1,
          supporters: [newSupporter],
        },
      ]
    }

    apolloClient.writeFragment({
      fragment: CommunityPostDataFragmentDoc,
      id: `PostsPost:${postId}`,
      fragmentName: 'CommunityPostData',
      data: {
        ...fragmentData,
        reactionsCount: fragmentData.reactionsCount + 1,
        newReactions: newReactionsToStore,
      },
    })
  }

  const getEmojiCode = (nativeEmoji: string) => {
    let emojiCode: string | undefined

    const allEmojis = emojiIndex.emojis
    const emojiData = Object.values(allEmojis).find((emoji) => {
      const found = 'native' in emoji && emoji.native === nativeEmoji

      if (found) return true

      if (1 in emoji) {
        return Object.values(emoji).find((em) => {
          return 'native' in em && em.native === nativeEmoji
        })
      }
    })

    if (!emojiData) return

    if ('colons' in emojiData) {
      emojiCode = emojiData.colons
    } else if (1 in emojiData) {
      const e = Object.values(emojiData).find((em) => {
        return 'native' in em && em.native === nativeEmoji
      })
      emojiCode = e && 'colons' in e ? e.colons : undefined
    }

    return emojiCode?.slice(1, emojiCode.length - 1)
  }

  const { performAction: toggleEmoji } = useAsyncAction(
    async (nativeEmoji: string) => {
      const code = getEmojiCode(nativeEmoji)

      if (!code) return

      const fragmentData = getCachedPost()

      if (!fragmentData) return

      const reactions = fragmentData.newReactions ?? []

      const emojisNum = reactions.length
      let reactionId: number | undefined

      for (let i = 0; i < emojisNum; i++) {
        if (reactions[i]?.postId === postId && reactions[i]?.code.startsWith(code)) {
          const reaction = reactions[i]
          reactionId = reaction?.supporters.find((supporter) => supporter.accessId === authorId)?.id
          break
        }
      }

      if (reactionId) {
        await deleteReactionMutation({
          variables: { id: reactionId },
        })

        removeReactionFromPost({
          postId,
          code,
        })
      } else {
        let eventType = COMMUNITY_ITEM_TYPES.REGULAR_POST

        if (fragmentData.type === 'Posts::V2::Birthday') {
          eventType = COMMUNITY_ITEM_TYPES.BIRTHDAY
        } else if (fragmentData.type === 'Posts::V2::Workiversary') {
          eventType = COMMUNITY_ITEM_TYPES.WORKIVERSARY
        } else if (fragmentData.type === 'Posts::V2::FirstDay') {
          eventType = COMMUNITY_ITEM_TYPES.NEW_JOINER
        }

        const eventWidget =
          eventType === COMMUNITY_ITEM_TYPES.REGULAR_POST
            ? COMMUNITY_WIDGET_TYPES.POST
            : COMMUNITY_WIDGET_TYPES.CELEBRATIONS

        track(trackingEvents.dashboard.reactToCommunityItem, {
          widget: eventWidget,
          type: eventType,
        })

        const { data } = await createReactionMutation({
          variables: { code, postId },
        })

        const newReaction = data?.postsMutations.createPostsReaction.reaction

        if (newReaction) {
          addReactionToPost({
            code,
            postId,
            reaction: {
              id: newReaction.id,
              author_id: newReaction.author?.id ?? authorId,
            },
          })
        }
      }
    },
    {
      successFlash: false,
    }
  )

  const onEmojiSelected = async (nativeEmoji: string) => {
    await asyncAction(() => toggleEmoji(nativeEmoji), {
      successFlash: false,
    })
  }

  return {
    onEmojiSelected,
  }
}
